/**
 * The referral reward as GET /qlearn/api/referral/stats reports it, read the
 * same way by the invite prompt and ShareEarnModal so the two never disagree.
 *
 * Durations are never hard-coded: they are server config. A reward counts as on
 * only when rewardPlanConfigured AND referralEnabled are both explicitly true; a
 * missing flag is "unknown", and an unknown reward is never promised. Each day
 * count must be a positive number, otherwise it is null and left unsaid.
 */

export const positiveDays = (value) => (
  typeof value === 'number' && Number.isFinite(value) && value > 0 ? value : null
);

export const daysOf = (n) => `${n} ${n === 1 ? 'day' : 'days'}`;

/**
 * @returns {{ on: boolean, inviterDays: number|null, friendDays: number|null }}
 *   inviterDays: rewardDaysPerReferral, granted when the friend finishes their
 *   first quiz. friendDays: refereeRewardDays, granted at signup.
 */
export const referralRewards = (stats) => {
  const on = Boolean(stats)
    && stats.rewardPlanConfigured === true
    && stats.referralEnabled === true;
  return {
    on,
    inviterDays: on ? positiveDays(stats.rewardDaysPerReferral) : null,
    friendDays: on ? positiveDays(stats.refereeRewardDays) : null,
  };
};

// LocalDateTime on the wire: an ISO string without a zone (what the subscriptions
// page formats with moment), or Jackson's [y, m, d, h, min, s] array if the
// deployment serialises dates as timestamps. Anything else is unknown (null).
const toDate = (value) => {
  let date = null;
  if (typeof value === 'string' || typeof value === 'number') {
    date = new Date(value);
  } else if (Array.isArray(value) && value.length >= 3) {
    const [y, m, d, h = 0, min = 0, sec = 0] = value;
    date = new Date(y, m - 1, d, h, min, sec);
  }
  return date && !Number.isNaN(date.getTime()) ? date : null;
};

/**
 * Whether the student is paying right now, judged from their SUBSCRIPTION
 * records: GET /user/{id}/plan?status=ACTIVE,CANCELLED,TRIALING, kept in the
 * store as state.getActivePlans.plansList. Each item is a
 * UserSubscriptionResponseDto (QLearn-service UserSubscriptionResponseDto.java):
 * { subscriptionId, subscriptionStartDt, subscriptionEndDt, plan: { category,
 * type, ... }, status, iselected, expired }.
 *
 * Not GET /user/{id}/plan/true (state.userPackage.userPackage). That returns Plan
 * records ({ category, type, status }, no dates) for every SELECTED
 * subscription, with no status or date filter (UserPlanService.java:56-63).
 * A referral-reward grant is created selected (ReferralService.grantProDays,
 * ReferralService.java:367-404) and nothing unselects it when its days run out,
 * so that list cannot tell a payer from an expired grant.
 *
 * Paid means: billed through Stripe (subscriptionId is set only on the Stripe
 * path, StripeSubscriptionEventService.java:324; referral and institutional
 * grants have none); ACTIVE, or CANCELLED with paid time left; not expired; an
 * end date that has not passed; on a non-FREE plan. TRIALING is not paid yet.
 * Known gap: App Store (RevenueCat) subscriptions store providerSubscriptionId,
 * which this DTO does not return, so they read as not paying.
 */
export const hasPaidSubscription = (subscriptions, now = new Date()) => (
  Array.isArray(subscriptions) && subscriptions.some((sub) => {
    if (!sub || typeof sub.subscriptionId !== 'string' || sub.subscriptionId === '') return false;
    if (sub.status !== 'ACTIVE' && sub.status !== 'CANCELLED') return false;
    if (sub.expired === true) return false;
    if (!sub.plan || !sub.plan.category || sub.plan.category === 'FREE') return false;
    const end = toDate(sub.subscriptionEndDt);
    return end !== null && end >= now;
  })
);
